import { useCallback, useEffect, useState } from 'react';
import { useCanUseTeamView } from 'abilities/team';
import {
  ProjectClips_V2Document,
  useCopyClipsMutation,
  useDeleteClipsMutation,
} from '../../components/StudioClipUserList/operation.generated';
import { usePromptsContext } from '../prompts/PromptsContext';
import {
  assertUserWillNotExceedClipGenerationLimit,
  TEAM_ERROR_MSG,
  USER_ERROR_MSG,
} from './useClipCreator';
import parseApolloError from '../../util/apolloError';
import { useProductUsageQuery } from '../../components/ProductUsage/operation.generated';
import { useIsManagedUser } from '../user/UserContext';
import { toast } from '../../components/Toaster';

type SubmitFunction = (destinationProjectId: string) => Promise<any>;
type copyStartFunction = () => void;
type copyEndFunction = () => void;

function useClipCopy(
  clipIds: Array<string>,
  onCopyStart: copyStartFunction,
  onCopyEnd: copyEndFunction
): [SubmitFunction] {
  const { data: usageData } = useProductUsageQuery();
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const [error, setError] = useState<string | undefined>();
  const [copyClips, { error: apolloError, data }] = useCopyClipsMutation();
  const [deleteClips] = useDeleteClipsMutation();

  // set error state when necessary
  useEffect(() => {
    if (apolloError) {
      setError(parseApolloError(apolloError));
    } else if (data && data.copyClipsToProject.success === false) {
      setError(data.copyClipsToProject.message);
    } else {
      setError(undefined);
    }
  }, [apolloError, data]);

  const canUseTeamView = useCanUseTeamView();

  const submitFn = useCallback(
    async (destinationProjectId: string) => {
      try {
        onCopyStart();
        if (usageData && usageData.user.productAccessLimits) {
          const teamErrorMessage = TEAM_ERROR_MSG;
          const userErrorMessage = USER_ERROR_MSG;

          assertUserWillNotExceedClipGenerationLimit({
            clipGenerationLimit:
              usageData.user.productAccessLimits.takeCountGenerationLimit ?? 0,
            clipsGenerated:
              usageData.user.productAccessPeriodUsage.clipsGenerated +
              usageData.user.productAccessPeriodUsage.takesGenerated,
            // in this case "attempingToRender" is actually "attemptingToCopy"
            clipsAttemptingToRender: clipIds.length,
            errorMessage: canUseTeamView ? teamErrorMessage : userErrorMessage,
            onBeforeThrow: () => {
              onCopyEnd();
              if (isManagedUser) {
                return;
              }
              if (!canUseTeamView) {
                dispatch({
                  type: 'OpenSubscriptionTiersPrompt',
                  payload: {
                    upgradeContext: 'access_limit_clips',
                    upgradePath: 'Dashboard > Studio > Clips Limit',
                  },
                });
              }
            },
          });

          const result = await copyClips({
            variables: {
              inputs: {
                clipIds,
                destinationProjectId,
              },
            },
            refetchQueries: [
              {
                query: ProjectClips_V2Document,
                variables: {
                  projectInputs: {
                    projectId: destinationProjectId,
                  },
                  clipsInputs: {},
                },
              },
            ],
          });
          onCopyEnd();
          if (result.data && !result.data.copyClipsToProject.success) {
            toast({
              message: result.data?.copyClipsToProject.message,
              type: 'error',
            });
          }
          if (result.data && result.data.copyClipsToProject.success) {
            // Logic for undo-ing the copy
            const undoCopyAction = async () => {
              const recentlyCreatedClipIds =
                result.data?.copyClipsToProject.clips?.map(
                  clip => clip.id
                ) as Array<string>;

              const undo = await deleteClips({
                variables: {
                  inputs: {
                    clipIds: recentlyCreatedClipIds,
                  },
                },
              });
              const numRecredited = undo.data?.deleteClips.numClipsRecredited;
              toast({
                message: `Undo successful. ${
                  numRecredited
                    ? `${numRecredited} clips recredited back to your account.`
                    : ''
                } `,
              });
            };
            const clips = result.data.copyClipsToProject?.clips;
            const projectName = clips ? clips[0].project.name : undefined;

            const clipText = clipIds.length === 1 ? 'clip' : 'clips';

            toast({
              message: `${clipIds.length} ${clipText} copied ${
                projectName ? `to ${projectName}` : ''
              } `,
              cta: {
                label: 'Undo',
                onClick: undoCopyAction,
              },
            });
          }
        }
      } catch (e) {
        if (!(e instanceof Error)) throw e;
        const { message } = e;
        setError(message);
        toast({
          message,
          type: 'error',
        });
        onCopyEnd();
      }
    },
    [
      clipIds,
      copyClips,
      deleteClips,
      dispatch,
      onCopyEnd,
      onCopyStart,
      usageData,
      isManagedUser,
      canUseTeamView,
    ]
  );

  return [submitFn];
}

export default useClipCopy;
